import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useDraggable } from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core'
import { db } from '../../../lib/db/indexeddb'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Task, Section } from '../../../lib/db/models'
import type { OptimisticUpdates } from './useTaskDragAndDrop'

export type OptimisticSectionUpdates = Map<string, { order: number }> | null

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseSectionDragParams {
  segments: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  wrapperRef: React.RefObject<HTMLDivElement | null>
  setOptimisticUpdates: Dispatch<SetStateAction<OptimisticUpdates>>
  setOptimisticSectionUpdates: Dispatch<SetStateAction<OptimisticSectionUpdates>>
}

/** Prefix for section draggable IDs — used to discriminate from task drags. */
export const SECTION_ID_PREFIX = 'section::'
/** Prefix for section drop zone IDs. */
export const SECTION_GAP_PREFIX = 'section-gap::'
/** Prefix for empty-section task drop zones — allows dropping tasks into sections with 0 tasks. */
export const EMPTY_SECTION_PREFIX = 'empty-section::'

/** FLIP drop animation data — captured before state clears, consumed by useSectionDragAnimation. */
export interface SectionDropAnim {
  sectionId: string
  x: number
  y: number
}

export function useSectionDrag({ segments, tasks, sections, wrapperRef, setOptimisticUpdates, setOptimisticSectionUpdates }: UseSectionDragParams) {
  // State drives re-renders (overlay, collapse classes, drop zone active state).
  const [activeSectionDragId, setActiveSectionDragId] = useState<string | null>(null)
  // Which drop zone the pointer is closest to (drives swap animation).
  const [hoverTargetIdx, setHoverTargetIdx] = useState<number | null>(null)
  // Ref is always current — immune to React batching / stale closure issues.
  const dragIdRef = useRef<string | null>(null)

  // Snapshot of segment DOMRects at drag start — used for counter-transforms & swap calc.
  const segmentRectsRef = useRef<Map<number, DOMRect>>(new Map())
  // Height of just the section header (not the full segment with tasks) — used for swap shift amounts.
  const sourceHeaderHeightRef = useRef<number>(0)
  // Y position of the source header BEFORE CSS changes — used to compute dynamic overlay offset.
  const sourceHeaderTopRef = useRef<number>(0)
  // FLIP drop animation: overlay position captured before state clears.
  const dropAnimRef = useRef<SectionDropAnim | null>(null)

  // ── Drag lifecycle handlers (called from the shared DndContext) ──

  /** Returns true if this event is a section drag (handled here), false if task drag. */
  const handleDragStart = useCallback((event: DragStartEvent): boolean => {
    const id = String(event.active.id)
    if (!id.startsWith(SECTION_ID_PREFIX)) return false
    const sectionId = id.slice(SECTION_ID_PREFIX.length)
    dragIdRef.current = sectionId

    // Snapshot all segment rects BEFORE React commits the CSS changes
    // (drop zones expand, margins collapse, source collapses).
    const wrapper = wrapperRef.current
    if (wrapper) {
      const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')
      segmentRectsRef.current.clear()
      segEls.forEach((el) => {
        const idx = parseInt(el.getAttribute('data-segment-idx')!, 10)
        segmentRectsRef.current.set(idx, el.getBoundingClientRect())
      })
      // Snapshot the source's section header height (just the header, not tasks).
      const headerEl = wrapper.querySelector<HTMLElement>(`.section-header[data-section-id="${sectionId}"]`)
      const headerRect = headerEl?.getBoundingClientRect()
      sourceHeaderHeightRef.current = headerRect?.height ?? 0
      sourceHeaderTopRef.current = headerRect?.top ?? 0
    }

    setActiveSectionDragId(sectionId)
    return true
  }, [wrapperRef])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : null
    if (!overId?.startsWith(SECTION_GAP_PREFIX)) {
      setHoverTargetIdx(null)
      return
    }
    const idx = parseInt(overId.slice(SECTION_GAP_PREFIX.length), 10)
    setHoverTargetIdx(idx)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dragId = dragIdRef.current
    if (!dragId) return

    // Capture overlay position for FLIP settle BEFORE clearing state
    const overlayEl = document.querySelector('.section-drag-overlay')
    if (overlayEl) {
      const rect = overlayEl.getBoundingClientRect()
      dropAnimRef.current = { sectionId: dragId, x: rect.left, y: rect.top }
    }

    // Clear ref + state
    dragIdRef.current = null
    setHoverTargetIdx(null)

    // Read drop target directly from the dnd-kit event
    const overId = event.over ? String(event.over.id) : null
    if (!overId?.startsWith(SECTION_GAP_PREFIX)) {
      setActiveSectionDragId(null)
      return
    }
    const targetIdx = parseInt(overId.slice(SECTION_GAP_PREFIX.length), 10)

    if (!tasks || !sections) {
      setActiveSectionDragId(null)
      return
    }

    // Find the source segment index
    const sourceSegIdx = segments.findIndex(seg => seg.section?.id === dragId)
    if (sourceSegIdx === -1) {
      setActiveSectionDragId(null)
      return
    }

    // Skip if dropping at the same position (before or after self)
    if (targetIdx === sourceSegIdx || targetIdx === sourceSegIdx + 1) {
      setActiveSectionDragId(null)
      return
    }

    const sourceSeg = segments[sourceSegIdx]

    // Build the new segment order (sections only, excluding unsectioned)
    const sectionSegments = segments.filter(s => s.section !== null)
    const sectionSourceIdx = sectionSegments.findIndex(s => s.section!.id === dragId)

    // Compute effective target in the section-only list
    let effectiveTarget = 0
    for (let i = 0; i < segments.length && i < targetIdx; i++) {
      if (segments[i].section !== null) effectiveTarget++
    }
    // Adjust for removal of the source
    if (sectionSourceIdx < effectiveTarget) effectiveTarget--

    // Build new section order
    const remaining = sectionSegments.filter(s => s.section!.id !== dragId)
    remaining.splice(effectiveTarget, 0, sourceSeg)

    // Build a complete map of section → ALL tasks (including collapsed/hidden).
    // segments[].tasks only contains visible tasks — collapsed sections have tasks=[]
    // but the tasks still exist in the global `tasks` array with their order values.
    type OrderItem = { type: 'task'; data: Task } | { type: 'section'; data: Section }
    const allItems: OrderItem[] = [
      ...tasks.map(t => ({ type: 'task' as const, data: t })),
      ...sections.map(s => ({ type: 'section' as const, data: s })),
    ]
    allItems.sort((a, b) => a.data.order - b.data.order)

    const fullTaskMap = new Map<string | null, Task[]>()
    let curSectionId: string | null = null
    for (const item of allItems) {
      if (item.type === 'section') {
        curSectionId = item.data.id
        if (!fullTaskMap.has(curSectionId)) fullTaskMap.set(curSectionId, [])
      } else {
        if (!fullTaskMap.has(curSectionId)) fullTaskMap.set(curSectionId, [])
        fullTaskMap.get(curSectionId)!.push(item.data as Task)
      }
    }

    // Calculate new order values for ALL items (sections + tasks) in the new sequence.
    const updates: Array<{ table: 'section' | 'task'; id: string; order: number }> = []
    let order = 0

    // Include unsectioned tasks first (they always come before sections)
    const unsectionedTasks = fullTaskMap.get(null) ?? []
    for (const t of unsectionedTasks) {
      order++
      if (t.order !== order) {
        updates.push({ table: 'task', id: t.id, order })
      }
    }

    for (const seg of remaining) {
      const sectionId = seg.section!.id
      order++
      if (seg.section!.order !== order) {
        updates.push({ table: 'section', id: sectionId, order })
      }
      const allSectionTasks = fullTaskMap.get(sectionId) ?? []
      for (const t of allSectionTasks) {
        order++
        if (t.order !== order) {
          updates.push({ table: 'task', id: t.id, order })
        }
      }
    }

    if (updates.length === 0) {
      setActiveSectionDragId(null)
      return
    }

    // ── Optimistic updates: apply synchronously so the UI reorders immediately ──
    const taskOptimistic = new Map<string, { order: number; sectionId?: string | null }>()
    const sectionOptimistic = new Map<string, { order: number }>()
    for (const u of updates) {
      if (u.table === 'task') {
        taskOptimistic.set(u.id, { order: u.order })
      } else {
        sectionOptimistic.set(u.id, { order: u.order })
      }
    }
    if (taskOptimistic.size > 0) setOptimisticUpdates(taskOptimistic)
    if (sectionOptimistic.size > 0) setOptimisticSectionUpdates(sectionOptimistic)

    // Clear drag state AFTER setting optimistic updates (same React batch)
    setActiveSectionDragId(null)

    // ── Async DB writes (fire-and-forget) ──
    // Batch task writes into a single reorderTasks call so Dexie triggers
    // useLiveQuery only once (avoids flickering from partial updates).
    const writeDb = async () => {
      const sectionWrites = updates.filter(u => u.table === 'section')
      const taskWrites = updates.filter(u => u.table === 'task')
      for (const u of sectionWrites) {
        await db.updateSection(u.id, { order: u.order })
      }
      if (taskWrites.length > 0) {
        await db.reorderTasks(taskWrites.map(u => ({ id: u.id, order: u.order })))
      }
      scheduleSync()
    }
    writeDb()
  }, [segments, tasks, sections, setOptimisticUpdates, setOptimisticSectionUpdates])

  const handleDragCancel = useCallback(() => {
    dragIdRef.current = null
    segmentRectsRef.current.clear()
    setHoverTargetIdx(null)
    setActiveSectionDragId(null)
  }, [])

  return {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart: handleDragStart,
    handleSectionDragOver: handleDragOver,
    handleSectionDragEnd: handleDragEnd,
    handleSectionDragCancel: handleDragCancel,
  }
}

/**
 * Hook for individual SectionHeader to register as draggable.
 * Call once per section in the segment map.
 */
export function useSectionDraggable(sectionId: string) {
  return useDraggable({ id: `${SECTION_ID_PREFIX}${sectionId}` })
}
